<template>
  <teleport to="#app">
    <color-picker
      v-bind="$attrs"
      ref="colorPicker"
      class="colorpicker-popout"
      :value="value"
      :style="{ left: x + xOffset + 'px', top: y + yOffset + 'px' }"
      :confirm-custom-use="true"
      :null-allowed="nullAllowed"
      :transparent="transparent"
      @update:value="updateColor($event)"
      @select="choose"
    />
  </teleport>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";

import ColorPicker from "@core/input/ColorPicker.vue";
import { onPointerDownOutside } from "@core/composables/onMouseDownOutside";

defineOptions({
  inheritAttrs: false
});

// PROPS
interface Props {
  value: string | null;
  x?: number;
  y?: number;
  nullAllowed?: boolean;
  transparent?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  x: 0,
  y: 0,
  nullAllowed: false,
  transparent: true
});

type C<T extends boolean> = T extends true ? string | null : string;
type EmittableColor = C<typeof props.nullAllowed>;

const emit = defineEmits<{
  (e: "update:value", value: EmittableColor): void;
  (e: "close"): void;
}>();

// DATA

const colorPicker = ref<InstanceType<typeof ColorPicker>>();
const xOffset = ref(0);
const yOffset = ref(0);

onMounted(() => {
  const el = colorPicker.value?.$el;
  if (el) {
    repositionIfNeeded(el);
    el.focus();
  }
});

const unbindCancel = onPointerDownOutside([() => colorPicker.value?.$el], () => {
  close();
});
onUnmounted(() => {
  unbindCancel();
});

function choose() {
  close();
}
function updateColor(color: EmittableColor) {
  emit("update:value", color);
}
function close() {
  emit("close");
}
function repositionIfNeeded(el: HTMLElement) {
  // make sure bottom does not leave screen
  const boundingRect = el.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;
  if (boundingRect.bottom > windowHeight) {
    yOffset.value = Math.round(windowHeight - boundingRect.bottom);
  }
  if (boundingRect.right > windowWidth) {
    xOffset.value = Math.round(windowWidth - boundingRect.right);
  }
}
</script>

<style lang="scss">
.colorpicker-popout {
  position: fixed;
  z-index: 5;
  box-shadow:
    0 2px 10px rgba(0, 0, 0, 0.12),
    0 2px 5px rgba(0, 0, 0, 0.16);
  padding-left: 5px;
  padding-right: 5px;
  background-color: var(--color-background-close);
  border-radius: 3px;
}
</style>
